import { z } from 'zod';
/**
 * Campo de documento: NORMALIZA antes de validar.
 *
 * O front manda o que o contador digitou, com ou sem pontuação; o domínio só
 * conhece dígitos. Fazer a normalização no schema — e não no service — garante
 * que TODA porta de entrada normalize igual, inclusive a que ainda não existe.
 *
 * A alternativa (regex exigindo dígitos crus) faria o endpoint recusar
 * `123.456.789-09`, que é exatamente o formato que a tela mostra.
 */
export const soDigitosSchema = (tamanho, rotulo) => z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => v.length === tamanho, `${rotulo} deve ter ${tamanho} dígitos`);
/**
 * Sexo da pessoa natural.
 *
 * ISTO NÃO É CAMPO DECORATIVO. É a chave da concordância de gênero em todo o
 * documento gerado. Sem ele o texto sai como saiu no `docs/amostras/`:
 * "o sócio único ... inscrita no CPF ... residente e domiciliada ... declara
 * que não está impedida". Sete erros num parágrafo, todos do mesmo campo
 * ausente. Ver `redacao/genero.ts`.
 */
/** Pessoa física ou jurídica — o discriminante da união de `pessoaSchema`. */
export const naturezaSchema = z.enum(['FISICA', 'JURIDICA']);
export const NATUREZAS = naturezaSchema.options;
export const sexoSchema = z.enum(['M', 'F']);
export const SEXOS = sexoSchema.options;
export const estadoCivilSchema = z.enum([
    'SOLTEIRO',
    'CASADO',
    'DIVORCIADO',
    'VIUVO',
    'SEPARADO',
    'UNIAO_ESTAVEL',
]);
export const ESTADOS_CIVIS = estadoCivilSchema.options;
/**
 * Regime de bens. Só existe quando o estado civil é CASADO, e importa
 * juridicamente: comunhão universal impede certos arranjos de quotas entre
 * cônjuges sócios (art. 977 do Código Civil).
 */
export const regimeBensSchema = z.enum([
    'COMUNHAO_PARCIAL',
    'COMUNHAO_UNIVERSAL',
    'SEPARACAO_TOTAL',
    'SEPARACAO_OBRIGATORIA',
    'PARTICIPACAO_FINAL',
]);
export const REGIMES_BENS = regimeBensSchema.options;
export const enderecoSchema = z.object({
    logradouro: z.string().min(1),
    numero: z.string().min(1),
    complemento: z.string().optional(),
    bairro: z.string().min(1),
    municipio: z.string().min(1),
    uf: z.string().length(2),
    /** Só dígitos. A formatação é responsabilidade da camada de redação. */
    cep: soDigitosSchema(8, 'CEP'),
});
/**
 * Pessoa natural. Sócio, administrador, procurador ou testemunha — o papel é
 * do vínculo, não da pessoa: a mesma pessoa pode ser sócia de uma sociedade e
 * administradora não sócia de outra.
 */
export const pessoaFisicaSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    /**
     * Número sequencial dentro do escritório, começando em 1 — o que a pessoa
     * cita ("a sociedade 42"). Nunca muda, e não se repete no mesmo tenant.
     * Ver `contadorTenant` no schema da API.
     */
    codigo: z.number().int().positive(),
    nome: z.string().min(3),
    sexo: sexoSchema,
    nacionalidade: z.string().min(1).default('brasileira'),
    naturalidadeMunicipio: z.string().min(1),
    naturalidadeUf: z.string().length(2),
    nascimento: z.string().date(),
    estadoCivil: estadoCivilSchema,
    regimeBens: regimeBensSchema.optional(),
    profissao: z.string().min(1),
    /** Só dígitos. */
    cpf: soDigitosSchema(11, 'CPF'),
    identidadeNumero: z.string().min(1),
    identidadeOrgao: z.string().min(1),
    endereco: enderecoSchema,
});
/** Sócio pessoa jurídica. Não tem sexo, nascimento nem estado civil. */
export const pessoaJuridicaSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    /** Mesmo contador da pessoa física — ver `pessoaFisicaSchema`. */
    codigo: z.number().int().positive(),
    razaoSocial: z.string().min(3),
    cnpj: soDigitosSchema(14, 'CNPJ'),
    nire: z.string().optional(),
    endereco: enderecoSchema,
});
/**
 * Rótulos em português dos enums de pessoa.
 *
 * Ficam AQUI e não na tela pelo mesmo motivo de `NOME_TIPO_SOCIETARIO`: o
 * documento gerado e a interface precisam dizer a mesma coisa. Duas listas
 * separadas divergem na primeira vez que alguém corrige uma só — e a
 * divergência aparece num contrato assinado, não num teste.
 *
 * `Record<X, string>` e não objeto solto: acrescentar um valor ao enum sem
 * rotular passa a ser erro de compilação.
 */
export const NOME_NATUREZA = {
    FISICA: 'Pessoa física',
    JURIDICA: 'Pessoa jurídica',
};
export const NOME_SEXO = {
    M: 'Masculino',
    F: 'Feminino',
};
export const NOME_ESTADO_CIVIL = {
    SOLTEIRO: 'Solteiro(a)',
    CASADO: 'Casado(a)',
    DIVORCIADO: 'Divorciado(a)',
    VIUVO: 'Viúvo(a)',
    SEPARADO: 'Separado(a)',
    UNIAO_ESTAVEL: 'União estável',
};
export const NOME_REGIME_BENS = {
    COMUNHAO_PARCIAL: 'Comunhão parcial de bens',
    COMUNHAO_UNIVERSAL: 'Comunhão universal de bens',
    SEPARACAO_TOTAL: 'Separação total de bens',
    SEPARACAO_OBRIGATORIA: 'Separação obrigatória de bens',
    PARTICIPACAO_FINAL: 'Participação final nos aquestos',
};
export const pessoaSchema = z.discriminatedUnion('natureza', [
    pessoaFisicaSchema.extend({ natureza: z.literal('FISICA') }),
    pessoaJuridicaSchema.extend({ natureza: z.literal('JURIDICA') }),
]);
export function ehFisica(p) {
    return p.natureza === 'FISICA';
}
/** Nome pelo qual a pessoa aparece no documento, seja PF ou PJ. */
export function nomeDe(p) {
    return ehFisica(p) ? p.nome : p.razaoSocial;
}
/**
 * Entrada de criação. Não tem `id` nem `tenantId` — os dois nascem no servidor.
 *
 * DTO que aceita `tenantId` é vazamento por construção: bastaria mandar o id de
 * outro escritório no corpo. Este campo não existe em nenhum DTO do projeto.
 */
export const criarPessoaFisicaSchema = pessoaFisicaSchema
    .omit({ id: true, tenantId: true, codigo: true })
    .extend({ natureza: z.literal('FISICA') });
export const criarPessoaJuridicaSchema = pessoaJuridicaSchema
    .omit({ id: true, tenantId: true, codigo: true })
    .extend({ natureza: z.literal('JURIDICA') });
export const criarPessoaSchema = z.discriminatedUnion('natureza', [
    criarPessoaFisicaSchema,
    criarPessoaJuridicaSchema,
]);
/**
 * Entrada de EDIÇÃO.
 *
 * Mesmo formato da criação, e não um `Partial`: o formulário edita a pessoa
 * inteira, e aceitar campos soltos abriria a porta para uma atualização que
 * apaga o que não foi enviado — o erro clássico de `PATCH` mal desenhado.
 *
 * A `natureza` continua no corpo, mas o servidor RECUSA trocá-la: pessoa
 * física não vira jurídica por edição. Seria outro cadastro, com outro
 * documento e outra qualificação — e o vínculo com as sociedades em que ela já
 * assina passaria a apontar para algo que não existia quando o contrato foi
 * assinado.
 */
export const atualizarPessoaSchema = criarPessoaSchema;
/**
 * Toda listagem é paginada. Nenhum `select` sem limite sai desta API.
 *
 * O teto é 200, não 100: a consulta de sociedades (`apps/web/src/paginas/
 * sociedades.tsx`) busca uma leva única e filtra no cliente — a API não tem
 * parâmetro de busca (ver docs/DIVIDA_TECNICA.md#d10) — então o teto aqui é o
 * tamanho da LEVA, não o de uma página de leitura.
 */
export const paginacaoSchema = z.object({
    pagina: z.coerce.number().int().min(1).default(1),
    tamanho: z.coerce.number().int().min(1).max(200).default(25),
});
//# sourceMappingURL=pessoa.js.map